import { styles } from '@/support/Style';
import { Avatar } from '@/support/Avatar';
import { Component, Vue, Watch } from 'vue-property-decorator';
import { RoleEnum, User } from '@/models/User';
import LanguageSwitch from '@/components/language-switch/LanguageSwitch';
import Breadcrumbs from '@/components/Breadcrumbs/Breadcrumbs';
import { Breadcrumb } from '@/support/Breadcrumb';

@Component<Navigation>({
  components: {
    LanguageSwitch,
    'mi-breadcrumbs': Breadcrumbs,
  },
})
export default class Navigation extends Vue {
  protected isMenuActive: boolean = false;
  protected breadcrumbs: Breadcrumb[] = [];
  protected isSettingsMenuOpen: boolean = false;
  protected temp: object = {};

  protected get settingItems(): NavigationItem[] {
    return [
      { title: this.$tc('hamburgerMenu.account'), link: this.profileLink, icon: 'face' },
      {
        title: this.$tc('hamburgerMenu.settings'), link: '/settings', icon: 'settings', visible: () => {
          return this.user.hasRole([RoleEnum.ADMINISTRATOR, RoleEnum.STYLIST, RoleEnum.SUPPORT]);
        }
      },
      {
        title: this.$tc('auth.logout'), action: () => {
          this.logout();
        }, icon: 'power_settings_new'
      },
    ];
  }
  get items(): NavigationItem[] {
    return [
      // { title: 'Dashboard', link: '/dashboard', icon: 'home' },
      {
        title: 'Leads',
        link: `/leads`,
        icon: 'list',
        category: 'leads',
        active: false,
        visible: (item: NavigationItem) => {
          return this.user.hasRole([RoleEnum.ADMINISTRATOR, RoleEnum.SUPPORT]);
        },
      },
      {
        title: this.$tc('contacts'),
        link: `/contacten`,
        icon: 'list',
        category: 'contact',
        active: false,
        visible: (item: NavigationItem) => {
          return this.user.hasRole([RoleEnum.STYLIST, RoleEnum.ADMINISTRATOR, RoleEnum.SUPPORT]);
        },
      },
      {
        title: 'Leveranciers',
        link: `/leveranciers`,
        icon: 'list',
        category: 'contact',
        active: false,
        visible: (item: NavigationItem) => {
          return this.user.hasRole([RoleEnum.ADMINISTRATOR, RoleEnum.SUPPORT]);
        },
      },
      {
        title: 'Gebruikers',
        link: '/gebruikers',
        active: false,
        category: 'users',
        visible: (item: NavigationItem) => {
          return this.user.hasRole([RoleEnum.ADMINISTRATOR]);
        },
        items: [
          {
            title: 'Stylisten',
            link: `/stylisten`,
            icon: 'list',
            category: 'users',
            active: false,
            visible: (item: NavigationItem) => {
              return this.user.hasRole(RoleEnum.ADMINISTRATOR)
            }
          },
          {
            title: 'Support',
            link: `/support`,
            icon: 'list',
            category: 'users',
            active: false,
            visible: (item: NavigationItem) => {
              return this.user.hasRole([RoleEnum.ADMINISTRATOR]);
            }
          },
          {
            title: 'Admin',
            link: `/admin`,
            icon: 'list',
            category: 'users',
            active: false,
            visible: (item: NavigationItem) => {
              return this.user.hasRole(RoleEnum.ADMINISTRATOR);
            }
          },
        ]
      },
      {
        title: this.$tc('menu.requests'),
        link: `/aanvragen`,
        icon: 'list',
        category: 'aanvragen',
        active: false,
        visible: (item: NavigationItem) => {
          return this.user.hasRole([RoleEnum.STYLIST, RoleEnum.ADMINISTRATOR, RoleEnum.SUPPORT]);
        },
      },
      {
        title: this.$tc('menu.orders'),
        link: `/orders`,
        icon: 'list',
        category: 'orders',
        active: false,
        visible: (item: NavigationItem) => {
          return this.user.hasRole([RoleEnum.STYLIST, RoleEnum.ADMINISTRATOR, RoleEnum.SUPPORT, RoleEnum.WAREHOUSE]);
        },
      },
      {
        title: 'Purchase Orders',
        link: `/purchase-orders`,
        icon: 'list',
        category: 'purchase-orders',
        active: false,
        visible: () => {
          return this.user.hasRole([RoleEnum.ADMINISTRATOR, RoleEnum.SUPPORT]);
        },
      },
      {
        title: 'Styling fees',
        link: `/styling-fees`,
        icon: 'list',
        category: 'order fee',
        active: false,
        visible: (item: NavigationItem) => {
          return this.user.hasRole([RoleEnum.ADMINISTRATOR, RoleEnum.SUPPORT]);
        },
      },
      {
        title: 'Products',
        link: `/producten`,
        icon: 'list',
        category: 'products',
        active: false,
        visible: (item: NavigationItem) => {
          return this.user.hasRole([RoleEnum.ADMINISTRATOR, RoleEnum.SUPPORT]);
        },
      },
      {
        title: 'Variants',
        link: `/varianten`,
        icon: 'list',
        category: 'variants',
        active: false,
        visible: (item: NavigationItem) => {
          return this.user.hasRole([RoleEnum.ADMINISTRATOR, RoleEnum.SUPPORT]);
        },
      },
    ];
  }

  protected get profileLink() {
    if (this.$store.state.Auth.hasRole(RoleEnum.ROOT) || this.$store.state.Auth.hasRole(RoleEnum.ADMINISTRATOR)) {
      return `/admin/${this.$store.state.Auth.id}`;
    }

    if (this.$store.state.Auth.hasRole(RoleEnum.SUPPORT)) {
      return `/support/${this.$store.state.Auth.id}`;
    }

    if (this.$store.state.Auth.hasRole(RoleEnum.STYLIST)) {
      return `/stylisten/${this.$store.state.Auth.id}`;
    }

    return `/contacten/${this.$store.state.Auth.id}`;
  }

  protected get userAvatar() {
    if (this.$store.state.Auth && this.$store.state.Auth.avatar && !this.$store.state.Auth.avatar.includes('gravatar')
    ) {
      return this.$store.state.Auth.avatar;
    }

    return new Avatar(this.$store.state.Auth.name, { color: styles['white'], background: styles['primary'] }).toDataUrl();
  }

  public mounted(): void {
    this.resetActive();
    this.setActiveItem();
    this.$root.$on('breadcrumbUpdated', (crumb: Breadcrumb[]) => {
      this.breadcrumbs = (crumb as any).crumb;
    })
  }

  protected logout() {
    this.$store.dispatch('userDeauthenticated');
    this.$router.push('/login');
  }

  protected canViewItem(item: NavigationItem) {
    return this.validateRole(item) && this.validateVisible(item);
  }

  protected validateRole(item: NavigationItem) {
    return !item.roles || this.user.hasRole(item.roles);
  }

  protected validateVisible(item: NavigationItem) {
    if (item.visible === undefined) {
      return true;
    }
    if (typeof item.visible === 'boolean') {
      return item.visible;
    }
    if (typeof item.visible === 'function') {
      return (item as any).visible(item);
    }
    return false;
  }

  protected createError() {
    throw "This is a custom error";
  }

  protected getTranslation(key: string) {
    return this.$t(key);
  }

  @Watch('$route')
  protected routerChanged(currentRoute: any, oldRoute: any) {
    if (oldRoute.meta.category) {
      const item = this.items.find((navItem: NavigationItem) => {
        return navItem.category === oldRoute.meta.category;
      });

      if (item) {
        item.active = false;
      }
    }

    this.resetActive();
    this.setActiveItem();

    if (this.isMenuActive) {
      this.isMenuActive = false;
    }
  }

  protected resetActive() {
    this.items.forEach((navItem: NavigationItem) => {
      navItem.active = false;
    });
  }

  protected setActiveItem() {
    // @ts-ignore
    if (!this.$router.currentRoute.meta.category) {
      return;
    }

    const item = this.items.find((navItem: NavigationItem) => {
      // @ts-ignore
      return navItem.category === this.$router.currentRoute.meta.category;
    });

    if (item) {
      item.active = true;
    }
  }

  protected get amountVisibleItems(): number {
    return this.items.reduce((accumulator, item) => accumulator + (this.validateVisible(item) ? 1 : 0), 0);
  }

  protected get isDrawerNavigation(): boolean {
    return this.amountVisibleItems > 3;
  }

  @Watch('drawerState')
  @Watch('miniState')
  @Watch('user')
  @Watch('schoolLogo')
  public onStateChanged(value: string, oldValue: string) { }

  get user(): User {
    return this.$store.state.Auth;
  }

  public displayItem(item: any) {
    if (item.auth === undefined) {
      return true;
    }

    return false;
  }
}

export interface NavigationItem {
  title: string;
  link?: string;
  icon?: string;
  roles?: RoleEnum[] | RoleEnum;
  visible?: Function | boolean;
  items?: NavigationItem[];
  external?: boolean;
  action?: Function;
  category?: string;
  active?: boolean;
}
